import { useState, useEffect, useRef, useCallback } from 'react';
import {
  type Board,
  type Notes,
  emptyBoard,
  emptyNotes,
  formatTime,
  isGiven,
  clearAutoNotes,
  toggleNoteLocal,
} from '@sudoku/shared';

// Re-export so existing imports from this file keep working
export type { Board, Notes };
export { emptyBoard, emptyNotes, formatTime, isGiven, clearAutoNotes, toggleNoteLocal };

interface UseGameBoardOptions {
  isPlaying: boolean;
  onPlace: (r: number, c: number, value: number) => void;
  onErase: (r: number, c: number) => void;
}

export function useGameBoard({ isPlaying, onPlace, onErase }: UseGameBoardOptions) {
  const [board, setBoard] = useState<Board>(emptyBoard);
  const [puzzle, setPuzzle] = useState<Board>(emptyBoard);
  const [notes, setNotes] = useState<Notes>(emptyNotes);
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(null);
  const [noteMode, setNoteMode] = useState(false);
  const [mistakes, setMistakes] = useState(0);
  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [wrongCell, setWrongCell] = useState<[number, number] | null>(null);
  const [highlightDigit, setHighlightDigit] = useState<number | null>(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [autoCleanNotes, setAutoCleanNotes] = useState(() => localStorage.getItem('sudoku_autoClean') !== 'false');
  const [highlightEnabled, setHighlightEnabled] = useState(() => localStorage.getItem('sudoku_highlight') !== 'false');
  const [cellHighlightEnabled, setCellHighlightEnabled] = useState(() => localStorage.getItem('sudoku_cellHighlight') !== 'false');
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { localStorage.setItem('sudoku_autoClean', String(autoCleanNotes)); }, [autoCleanNotes]);
  useEffect(() => { localStorage.setItem('sudoku_highlight', String(highlightEnabled)); }, [highlightEnabled]);
  useEffect(() => { localStorage.setItem('sudoku_cellHighlight', String(cellHighlightEnabled)); }, [cellHighlightEnabled]);

  // Timer
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isPlaying, isPaused]);

  // Klavye
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isPlaying || isPaused) return;
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        onPlace(selectedCell?.[0] ?? -1, selectedCell?.[1] ?? -1, num);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        onErase(selectedCell?.[0] ?? -1, selectedCell?.[1] ?? -1);
      } else if (e.key === 'n' || e.key === 'N') {
        setNoteMode(prev => !prev);
      } else if (selectedCell) {
        if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedCell([Math.max(0, selectedCell[0] - 1), selectedCell[1]]); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedCell([Math.min(8, selectedCell[0] + 1), selectedCell[1]]); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); setSelectedCell([selectedCell[0], Math.max(0, selectedCell[1] - 1)]); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); setSelectedCell([selectedCell[0], Math.min(8, selectedCell[1] + 1)]); }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isPlaying, isPaused, selectedCell, onPlace, onErase]);

  const getCellClass = useCallback((r: number, c: number) => {
    const classes = ['game-cell'];
    if (isGiven(puzzle, r, c)) classes.push('given');
    if (selectedCell && selectedCell[0] === r && selectedCell[1] === c) classes.push('selected');
    if (cellHighlightEnabled && selectedCell) {
      const [sr, sc] = selectedCell;
      if (r === sr || c === sc || (Math.floor(r / 3) === Math.floor(sr / 3) && Math.floor(c / 3) === Math.floor(sc / 3))) {
        classes.push('highlighted');
      }
    }
    if (wrongCell && wrongCell[0] === r && wrongCell[1] === c) classes.push('wrong');
    if (highlightEnabled && highlightDigit && board[r]?.[c] === highlightDigit) classes.push('digit-highlight');
    return classes.join(' ');
  }, [puzzle, selectedCell, cellHighlightEnabled, wrongCell, highlightEnabled, highlightDigit, board]);

  const handleCellClick = useCallback((r: number, c: number) => {
    setSelectedCell([r, c]);
    if (board[r]?.[c]) setHighlightDigit(board[r][c]);
    else setHighlightDigit(null);
  }, [board]);

  const digitCounts = Array.from({ length: 10 }, () => 0);
  for (let r = 0; r < 9; r++)
    for (let c = 0; c < 9; c++)
      if (board[r]?.[c]) digitCounts[board[r][c]!]++;

  const resetBoard = useCallback((
    newPuzzle: Board,
    newBoard?: Board,
    newNotes?: Notes,
    opts?: { elapsed?: number; mistakes?: number; hintsUsed?: number; score?: number },
  ) => {
    setPuzzle(newPuzzle.map(r => [...r]));
    setBoard((newBoard || newPuzzle).map(r => [...r]));
    setNotes(newNotes || emptyNotes());
    setMistakes(opts?.mistakes ?? 0);
    setScore(opts?.score ?? 0);
    setElapsed(opts?.elapsed ?? 0);
    setHintsUsed(opts?.hintsUsed ?? 0);
    setWrongCell(null);
    setHighlightDigit(null);
    setShowCandidates(false);
    setIsPaused(false);
    setNoteMode(false);
    // Ilk bos hucreyi otomatik sec
    const puz = newPuzzle;
    let autoSelected = false;
    for (let r = 0; r < 9 && !autoSelected; r++) {
      for (let c = 0; c < 9 && !autoSelected; c++) {
        if (!isGiven(puz, r, c) && ((newBoard || newPuzzle)[r][c] == null || (newBoard || newPuzzle)[r][c] === 0)) {
          setSelectedCell([r, c]);
          autoSelected = true;
        }
      }
    }
    if (!autoSelected) setSelectedCell(null);
  }, []);

  return {
    // State
    board, setBoard,
    puzzle, setPuzzle,
    notes, setNotes,
    selectedCell, setSelectedCell,
    noteMode, setNoteMode,
    mistakes, setMistakes,
    score, setScore,
    elapsed, setElapsed,
    wrongCell, setWrongCell,
    highlightDigit, setHighlightDigit,
    hintsUsed, setHintsUsed,
    isPaused, setIsPaused,
    showCandidates, setShowCandidates,
    autoCleanNotes, setAutoCleanNotes,
    highlightEnabled, setHighlightEnabled,
    cellHighlightEnabled, setCellHighlightEnabled,
    showSettings, setShowSettings,
    settingsRef,
    timerRef,
    // Computed
    digitCounts,
    // Functions
    getCellClass,
    handleCellClick,
    resetBoard,
  };
}
